import React from "react";
import { useSearchParams } from "react-router-dom";
import { TopBar } from "@/components/TopBar";
import { useAuth } from "@/context/AuthContext";
import { usePengaturan } from "@/hooks/usePengaturan";
import { ROLE_LABELS } from "@/lib/roles";
import { cekSensitif, jenisPerangkat, panduanPeran, panduanUmum, susunLaporan, waLaporan } from "@/lib/bantuan";
import * as W from "@/lib/waktu";
import { BookOpen, Copy, LifeBuoy, MessageCircle, ShieldAlert } from "lucide-react";

// owner 2026-10-15 (TAMBAHAN-OWNER-OKT A3): the guide of this role + a problem report for
// Tim iartech. The report is built here and sent by the user (WhatsApp or copy); it holds
// no username, customer data or password — risky text is refused before it can leave.
const Daftar = ({ judul, butir, testid, warna = "text-slate-200" }) =>
  butir.length ? (
    <div data-testid={testid}>
      <h3 className="mb-1.5 text-[13px] font-bold text-soq-biru">{judul}</h3>
      <ul className={`list-disc space-y-1 pl-5 text-sm ${warna}`}>{butir.map((b) => <li key={b}>{b}</li>)}</ul>
    </div>
  ) : null;

export default function Bantuan() {
  const { user } = useAuth();
  const cfg = usePengaturan();
  const [sp] = useSearchParams();
  const [apa, setApa] = React.useState("");
  const [dicoba, setDicoba] = React.useState("");
  const [menu, setMenu] = React.useState(sp.get("dari") || "");
  const [pesan, setPesan] = React.useState(null);
  const p = panduanPeran(user?.role);
  const umum = panduanUmum();

  const masalah = cekSensitif(`${apa}\n${dicoba}\n${menu}`);
  const teks = susunLaporan({
    apa, sudahDicoba: dicoba, menu, waktu: `${W.waktu(new Date().toISOString())} ${W.zonaSingkat()}`,
    peran: ROLE_LABELS[user?.role] || user?.role, versi: process.env.REACT_APP_VERSI || "-",
    perangkat: jenisPerangkat(navigator.userAgent),
  });
  const siap = apa.trim().length >= 10 && masalah.length === 0;
  const wa = waLaporan(cfg?.bantuan_wa, teks);

  const salin = async () => {
    try { await navigator.clipboard.writeText(teks); setPesan("Laporan disalin. Tempel di WhatsApp Tim iartech."); }
    catch { setPesan("Tidak bisa menyalin otomatis. Blok teks di kotak pratinjau lalu salin."); }
  };

  return (
    <div data-testid="bantuan-page" className="min-h-screen bg-slate-950 text-slate-200">
      <TopBar />
      <main className="mx-auto max-w-4xl space-y-6 px-4 py-6 sm:px-6 lg:px-8">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-bold tracking-tight text-slate-100 sm:text-3xl"><LifeBuoy size={26} className="text-soq-oranye" /> Bantuan kendala</h1>
          <p className="mt-1 text-sm text-slate-400">Panduan singkat untuk peran Anda, lalu cara melapor kendala ke Tim iartech.</p>
        </div>

        <section data-testid="bantuan-panduan" className="space-y-4 rounded-xl border border-slate-800 bg-slate-900/60 p-5">
          <h2 className="flex items-center gap-2 text-lg font-bold text-slate-100"><BookOpen size={18} /> Panduan {p.judul}</h2>
          <ol data-testid="bantuan-langkah" className="list-decimal space-y-1.5 pl-5 text-sm text-slate-200">{p.langkah.map((b) => <li key={b}>{b}</li>)}</ol>
          <Daftar judul="Tidak boleh" butir={p.tidakBoleh} testid="bantuan-tidak-boleh" warna="text-red-700" />
          <Daftar judul="Kalau ada masalah" butir={p.masalah} testid="bantuan-masalah" />
          <Daftar judul={umum.judul} butir={umum.butir} testid="bantuan-umum" />
        </section>

        <section data-testid="bantuan-lapor" className="space-y-4 rounded-xl border border-soq-oranye/50 bg-white p-5">
          <h2 className="flex items-center gap-2 text-lg font-bold text-slate-100"><MessageCircle size={18} /> Lapor kendala ke Tim iartech</h2>
          <div className="flex gap-2 rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-sm text-amber-300">
            <ShieldAlert size={18} className="mt-0.5 shrink-0" />
            <span>Jangan tulis password, nomor HP, alamat, atau rekening customer. Cukup sebut <b>nomor faktur atau SPK</b> dan apa yang terjadi. Jangan kirim foto nota.</span>
          </div>
          <label className="block text-sm">
            <span className="mb-1 block font-semibold text-slate-200">Apa yang terjadi? (wajib, minimal 10 huruf)</span>
            <textarea data-testid="bantuan-apa" rows={4} value={apa} onChange={(e) => setApa(e.target.value)}
              placeholder="mis. Faktur JRS20261015003: tombol Selesai di pos Press tidak bisa ditekan, muncul tulisan merah ..."
              className="w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-100" />
          </label>
          <div className="grid gap-4 sm:grid-cols-2">
            <label className="block text-sm">
              <span className="mb-1 block font-semibold text-slate-200">Menu / halaman</span>
              <input data-testid="bantuan-menu" value={menu} onChange={(e) => setMenu(e.target.value)} placeholder="mis. Antrean"
                className="w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-100" />
            </label>
            <label className="block text-sm">
              <span className="mb-1 block font-semibold text-slate-200">Yang sudah dicoba (boleh kosong)</span>
              <input data-testid="bantuan-dicoba" value={dicoba} onChange={(e) => setDicoba(e.target.value)} placeholder="mis. muat ulang, logout lalu login"
                className="w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-100" />
            </label>
          </div>
          {masalah.length > 0 && (
            <p data-testid="bantuan-sensitif" className="rounded-lg border border-red-500/40 bg-red-500/10 px-3 py-2 text-sm text-red-700">
              Hapus dulu dari laporan: <b>{masalah.join(", ")}</b>. Laporan tidak bisa dikirim selama masih ada data ini.
            </p>
          )}
          <div>
            <span className="mb-1 block text-xs font-semibold text-slate-500">Pratinjau laporan (yang terkirim hanya teks ini)</span>
            <pre data-testid="bantuan-pratinjau" className="whitespace-pre-wrap rounded-lg border border-slate-700 bg-slate-950 p-3 text-xs text-slate-300">{teks}</pre>
          </div>
          <div className="flex flex-wrap gap-2">
            {wa ? (
              <a data-testid="bantuan-kirim-wa" href={siap ? wa : undefined} target="_blank" rel="noopener noreferrer" aria-disabled={!siap}
                onClick={(e) => { if (!siap) e.preventDefault(); }}
                className={`inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold ${siap ? "bg-emerald-500 text-slate-950 hover:bg-emerald-400" : "cursor-not-allowed bg-slate-800 text-slate-500"}`}>
                <MessageCircle size={15} /> Kirim lewat WhatsApp
              </a>
            ) : (
              <span data-testid="bantuan-tanpa-wa" className="self-center text-xs text-slate-500">Nomor WhatsApp Tim iartech belum diisi Owner (Pengaturan). Pakai tombol Salin.</span>
            )}
            <button data-testid="bantuan-salin" type="button" disabled={!siap} onClick={salin}
              className="inline-flex items-center gap-2 rounded-lg border border-slate-700 px-4 py-2 text-sm text-slate-200 hover:border-amber-500/50 disabled:opacity-40">
              <Copy size={15} /> Salin laporan
            </button>
          </div>
          {pesan && <p data-testid="bantuan-pesan" className="text-sm text-emerald-600">{pesan}</p>}
          <p className="text-xs text-slate-500">Laporan dikirim dari WhatsApp Anda sendiri. Aplikasi tidak menyimpan dan tidak mengirim laporan ini.</p>
        </section>
      </main>
    </div>
  );
}
